<script setup lang="ts">
import type { IdeaWithVotes, SortOption } from '../types';
import IdeaListTable from './IdeaListTable.vue';
import IdeaListCard from './IdeaListCard.vue';

interface Props {
  ideas: IdeaWithVotes[];
  isMobile?: boolean;
  visibleVoteTypes?: ('liked' | 'important' | 'supported')[];
  currentUserId?: string;
  userRole?: 'owner' | 'member' | 'reader' | null;
  sortBy?: SortOption;
  votesLocked?: boolean;
  commentsEnabled?: boolean;
}

withDefaults(defineProps<Props>(), {
  isMobile: false,
  visibleVoteTypes: () => ['liked', 'important', 'supported'],
  votesLocked: false,
  commentsEnabled: true,
});

const emit = defineEmits({
  vote: (
    _id: string,
    _type: 'liked' | 'important' | 'supported',
    _onError: (() => void) | undefined
  ) => true,
  edit: (_id: string) => true,
  delete: (_id: string) => true,
  tagClick: (_tag: string) => true,
  'update:sortBy': (_sort: SortOption) => true,
  comment: (_id: string) => true,
});
</script>

<template>
  <div class="space-y-3">
    <div class="hidden md:block">
      <IdeaListTable
        :ideas="ideas"
        :visible-vote-types="visibleVoteTypes"
        :current-user-id="currentUserId"
        :user-role="userRole"
        :sort-by="sortBy"
        :votes-locked="votesLocked"
        :comments-enabled="commentsEnabled"
        @vote="(id, type, onError) => emit('vote', id, type, onError)"
        @edit="id => emit('edit', id)"
        @delete="id => emit('delete', id)"
        @tag-click="tag => emit('tagClick', tag)"
        @update:sort-by="sort => emit('update:sortBy', sort)"
        @comment="id => emit('comment', id)"
      />
    </div>
    <div class="md:hidden space-y-3">
      <IdeaListCard
        v-for="idea in ideas"
        :key="idea.id"
        :idea="idea"
        :visible-vote-types="visibleVoteTypes"
        :current-user-id="currentUserId"
        :user-role="userRole"
        :votes-locked="votesLocked"
        :comments-enabled="commentsEnabled"
        @vote="(id, type, onError) => emit('vote', id, type, onError)"
        @edit="id => emit('edit', id)"
        @delete="id => emit('delete', id)"
        @tag-click="tag => emit('tagClick', tag)"
        @comment="id => emit('comment', id)"
      />
    </div>
  </div>
</template>
